import {
    Alert,
    Box,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { Download, PrivacyTip } from '@mui/icons-material'

import { useDownloadPrivacyEvidenceHtmlMutation, useGetPrivacyEvidenceQuery } from '@/apis/opsApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { useReportArgs } from '@/hooks/useReportArgs'
import { Kpi, ReportStatus, SectionCard, fmtNum } from '@/modules/reports/common'
import { downloadText } from '@/utils/download'
import type { Period } from '@/utils/dates'

import { PrivacyEventsCard } from './PrivacyEventsCard'
import { PrivacySettingsCard } from './PrivacySettingsCard'
import { privacyVerdict } from './proofModel'

/** v2 `/reports/privacy-evidence`: what visitors asked for and what YOM did, per period. */
export function PrivacyEvidenceTab({ period }: { period: Period }) {
    const args = useReportArgs(period)
    const dispatch = useAppDispatch()
    const { data, isLoading, isFetching, error } = useGetPrivacyEvidenceQuery(args)
    const [downloadHtml, htmlState] = useDownloadPrivacyEvidenceHtmlMutation()

    const exportHtml = async () => {
        try {
            const html = await downloadHtml(args).unwrap()
            // Downloaded, not opened from a blob: URL, so the report never runs in our origin.
            downloadText(`opt-out-evidence-${args.from}-${args.to}.html`, html, 'text/html')
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Download failed',
                    severity: 'error',
                }),
            )
        }
    }

    const t = data?.totals
    const verdict = t ? privacyVerdict(t) : null
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<Download />}
                    disabled={htmlState.isLoading || !data}
                    onClick={() => {
                        void exportHtml()
                    }}
                >
                    Printable evidence (HTML)
                </Button>
            </Box>
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!!t && t.accepted === 0 && t.actions.dropped.total === 0}
                emptyIcon={<PrivacyTip />}
                emptyTitle="No traffic in this range"
                emptyDescription="Evidence is recorded for every accepted or dropped event."
            >
                {data && t && verdict && (
                    <Box sx={{ opacity: isFetching ? 0.7 : 1 }}>
                        <Alert severity={verdict.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
                            {verdict.text}
                            {data.sampled
                                ? data.exact_from
                                    ? ` Exact from ${data.exact_from}; earlier days are sampling-weighted estimates.`
                                    : ' Counts are sampling-weighted estimates.'
                                : ' Counts are exact.'}
                        </Alert>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi title="Events accepted" value={fmtNum(t.accepted)} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Opt-out requests"
                                    value={fmtNum(t.opt_outs.requests)}
                                    hint={`GPC ${fmtNum(t.signals.gpc)} · DNT ${fmtNum(t.signals.dnt)} · consent denied ${fmtNum(t.consent.denied)}`}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Stored cookieless"
                                    value={fmtNum(t.actions.cookieless)}
                                    hint={`${fmtNum(t.actions.dropped.total)} dropped by policy`}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Withheld from ad platforms"
                                    value={fmtNum(t.actions.ad_platform_suppressed)}
                                    exact
                                    hint={`${fmtNum(t.actions.ad_platform_suppressed_gpc)} because of GPC; counted per destination`}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 5 }}>
                                <SectionCard title="Policy in force">
                                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                        {data.policy.statements.map(s => (
                                            <Typography component="li" variant="body2" key={s}>
                                                {s}
                                            </Typography>
                                        ))}
                                    </Box>
                                </SectionCard>
                            </Grid>
                            <Grid size={{ xs: 12, md: 7 }}>
                                <SectionCard title="By day">
                                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Day</TableCell>
                                                    <TableCell align="right">Accepted</TableCell>
                                                    <TableCell align="right">Opt-outs</TableCell>
                                                    <TableCell align="right">Honored</TableCell>
                                                    <TableCell align="right">
                                                        PII scrubbed
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.by_day.map(d => (
                                                    <TableRow key={d.day}>
                                                        <TableCell>{d.day}</TableCell>
                                                        <TableCell align="right">
                                                            {fmtNum(d.accepted)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {fmtNum(d.opt_outs.requests)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {fmtNum(d.opt_outs.honored)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {fmtNum(d.actions.pii_fields_scrubbed)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </SectionCard>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </ReportStatus>
            <Box sx={{ mt: 2 }}>
                <PrivacyEventsCard args={args} />
            </Box>
            <Box sx={{ mt: 2 }}>
                <PrivacySettingsCard />
            </Box>
        </Box>
    )
}
